import * as curve from "@visx/curve";
import upperFirst from "lodash/upperFirst";
import type { AxisScale } from "@visx/axis";
import type { Margin, NearestDatumArgs, NearestDatumReturnType } from "@visx/xychart";
import type {
  ArcScaleArgs,
  BisectorResult,
  ConstructScaleLinear,
  ConstructScaleTime,
  CurveFactories,
  CurveType,
  LayoutType,
} from "./ChartHelper.types";
import { Delaunay } from "@visx/vendor/d3-delaunay";
import { scaleTime, scaleLinear } from "@visx/vendor/d3-scale";
import { bisector as d3Bisector, extent } from "@visx/vendor/d3-array";
import type { ScaleTime } from "@visx/vendor/d3-scale";

const CURVE_FACTORIES: CurveFactories = {
  curveBasisClosed: curve.curveBasisClosed,
  curveBasisOpen: curve.curveBasisOpen,
  curveBasis: curve.curveBasis,
  curveLinearClosed: curve.curveLinearClosed,
  curveLinear: curve.curveLinear,
  curveMonotoneX: curve.curveMonotoneX,
  curveMonotoneY: curve.curveMonotoneY,
  curveNatural: curve.curveNatural,
  curveStep: curve.curveStep,
  curveStepAfter: curve.curveStepAfter,
  curveStepBefore: curve.curveStepBefore,
};

class ChartHelpers {
  static getCurveFactory(type: CurveType, layout?: LayoutType) {
    if (typeof type === "function") {
      return type;
    }

    const name = `curve${upperFirst(type)}`;

    if ((name === "curveMonotone" || name === "curveBump") && layout) {
      return CURVE_FACTORIES[`${name}${layout === "vertical" ? "Y" : "X"}`];
    }
    return CURVE_FACTORIES[name] || curve.curveLinear;
  }

  static bisector<T>(
    data: T[],
    x: ScaleTime<number, number>,
    mx: number,
    accessor: (d: T) => Date
  ): BisectorResult<T> | null {
    const bisect = d3Bisector(accessor).right;

    const x0 = x.invert(mx);
    const i = bisect(data, x0, 1);
    const d0 = data[i - 1];
    const d1 = data[i];

    if (!d0 && !d1) return null;

    let datum = d0;
    let index = i - 1;

    if (d1 && accessor(d1)) {
      const x0Value = x0.valueOf();
      const d0Value = accessor(d0).valueOf();
      const d1Value = accessor(d1).valueOf();

      if (x0Value - d0Value > d1Value - x0Value) {
        datum = d1;
        index = i;
      }
    }

    return { index, datum };
  }

  static findNearestDatumXY<XScale extends AxisScale, YScale extends AxisScale, Datum extends object>({
    width,
    height,
    xScale,
    yScale,
    xAccessor,
    yAccessor,
    point,
    data,
  }: NearestDatumArgs<XScale, YScale, Datum>): NearestDatumReturnType<Datum> {
    if (!point) return null;

    const scaledX = (d: Datum) => Number(xScale(xAccessor(d)));
    const scaledY = (d: Datum) => Number(yScale(yAccessor(d)));

    const voronoiFrom = Delaunay.from(data, scaledX, scaledY).voronoi([0, 0, width, height]);

    const index = voronoiFrom.delaunay.find(point.x, point.y);
    const datum = data[index];
    if (!datum) return null;

    const distanceX = Math.abs(scaledX(datum) - point.x);
    const distanceY = Math.abs(scaledY(datum) - point.y);

    return { datum, index, distanceX, distanceY };
  }

  static getMinMaxValues<T>(data: T[], accessor: (d: T) => number) {
    return extent(data, accessor);
  }

  static constructScaleLinear<T>(args: ConstructScaleLinear<T>) {
    const { data, accessor, height, margin, plotFromZero = false, reverse = false } = args;

    const [min, max] = ChartHelpers.getMinMaxValues(data, accessor);

    // Set the min and max values of the y-axis to the min and max values of the data if they are not set. Otherwise, use the provided values.
    const minVal = plotFromZero ? 0 : args.minValue || min || 0;
    const maxVal = args.maxValue || max || 0;

    const domain = reverse ? [maxVal, minVal] : [minVal, maxVal];

    return scaleLinear<number>()
      .domain(domain)
      .range([height - margin.top, margin.bottom]);
  }

  static constructScaleTime<T>(args: ConstructScaleTime<T>) {
    const { data, accessor, width, margin } = args;

    const { start, end } = args;

    const [earliestData, latestData] = extent(data, accessor) as [Date, Date];

    return scaleTime<number>()
      .domain([start || earliestData, end || latestData])
      .range([margin.left, width - margin.right]);
  }

  static getInnerDimensions(width: number, height: number, margin: Margin) {
    return {
      innerWidth: width - margin.left - margin.right,
      innerHeight: height - margin.top - margin.bottom,
    };
  }

  /**
   * Arc thickness should be around 75% of the radius.
   */
  static getArcCenter(width: number, height: number) {
    return { x: width / 2, y: height / 2 };
  }

  static getOuterRadius(width: number, height: number) {
    return Math.min(width, height) / 2;
  }

  static getInnerRadius(radius: number) {
    return radius * 0.75;
  }

  static getArcAngle(angle: number) {
    return (Math.PI * angle) / 180;
  }

  /**
   * @description Builds an arc scale for the gauge chart.
   *
   * @param args The arguments needed to build the arc scale.
   *
   */
  static constructArcScale(args: ArcScaleArgs) {
    const { maxValue, minValue, endAngle, startAngle, options } = args;
    const { clamp = true } = options || {};

    const range = [startAngle, endAngle];
    const domain = [minValue, maxValue];

    return scaleLinear().domain(domain).range(range).clamp(clamp);
  }
}

export default ChartHelpers;
